import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ChevronDown } from 'lucide-react';
import React, { use, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../components/ui/button';
import { clone, diffInObjects, setNestedValue, toFormrValue, typeDefault } from './formr.utils';
import { formrSettings } from './FormrSettings';
import { FormrCtx } from './FormrProvider';
import { shadcnRenderers } from './renderers';
const toLabel = (key) => {
    const dotToSpace = key.replace(/([\\.])/g, ' - ').trim();
    const spaceBeforeUppercase = dotToSpace.replace(/([A-Z])/g, ' $1').trim();
    return (spaceBeforeUppercase.charAt(0).toUpperCase() +
        spaceBeforeUppercase.slice(1).toLowerCase());
};
const detectFormrType = (data) => {
    switch (typeof data) {
        case 'string':
            return 'string';
        case 'number':
            return 'number';
        case 'boolean':
            return 'boolean';
        default:
            return 'string';
    }
};
const toFormrField = (key, value, order) => {
    const detectedType = detectFormrType(value);
    return {
        label: toLabel(key),
        key: key,
        autoFocus: false,
        order: order,
        defaultValue: typeDefault(detectedType),
        editable: () => true,
        hidden: () => false,
        listView: 'always',
        type: detectedType,
        options: [],
        getter: (fieldValue) => fieldValue,
        setter: (userValue) => userValue,
        validator: () => {
            return { isValid: true, message: '' };
        }
    };
};
const toFormrFields = (data, prefix = '', startIndex = 100, indexSteps = 100) => {
    const fields = [];
    Object.entries(data)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .forEach((entry, index) => {
        const order = startIndex + index * indexSteps;
        const key = prefix + entry[0];
        const value = entry[1];
        if (typeof value === 'object') {
            fields.push(...toFormrFields(value, key + '.', order, indexSteps / 10));
        }
        else {
            fields.push(toFormrField(key, value, order));
        }
    });
    return fields;
};
const toFormrDef = (data) => {
    const fields = toFormrFields(data);
    return {
        fields: new Map(fields.map((f) => [f.key, f]))
    };
};
const FormrInput = ({ formrValue, onUpdate, readonly = false, renderers }) => {
    // Use editAs if provided and field is editable
    if (formrValue.editAs && !readonly && formrValue.editable(formrValue.value)) {
        return _jsx(_Fragment, { children: formrValue.editAs(formrValue.value) });
    }
    const Renderer = renderers[formrValue.type];
    if (!Renderer) {
        return _jsx("div", {});
    }
    return (_jsx(Renderer, { formrValue: formrValue, onUpdate: onUpdate, readonly: readonly }));
};
/** Split the ordered fields into runs sharing the same (or no) section. */
const groupBySection = (values) => {
    const groups = [];
    for (const value of values) {
        const last = groups[groups.length - 1];
        if (last && last.section === value.section) {
            last.values.push(value);
        }
        else {
            groups.push({ section: value.section, values: [value] });
        }
    }
    return groups;
};
/**
 * A collapsible group of fields. Closed by default — the point is to keep the
 * rarely used settings out of the way — but held open while a field inside is
 * invalid, so a validation error cannot hide behind a folded section leaving
 * the save button disabled with no visible reason. The content stays mounted
 * when folded so renderer-local state (an expanded sub-editor, say) survives
 * a toggle.
 */
const FormrSection = ({ label, forceOpen, children }) => {
    const [open, setOpen] = useState(false);
    const isOpen = open || forceOpen;
    // Styled like VAccordion: a gray header bar that carries the rounding, and
    // a bottom-rounded bordered panel when open.
    return (_jsxs("fieldset", { children: [_jsxs("button", { type: "button", className: `flex w-full items-center justify-between rounded-md bg-muted/60 px-4 py-2.5 text-sm font-medium hover:bg-muted ${isOpen ? 'rounded-b-none' : ''}`, onClick: () => setOpen((old) => !old), "aria-expanded": isOpen, children: [label, _jsx(ChevronDown, { className: `h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}` })] }), _jsx("div", { className: isOpen
                    ? 'space-y-3 rounded-b-md border border-t-0 border-muted p-4'
                    : 'hidden', children: children })] }));
};
const validate = (formValidator, formrValues, internalCopy) => {
    const allFieldsAreValid = formrValues.every((f) => {
        return f.validator(f.value).isValid;
    });
    return formValidator(internalCopy) && allFieldsAreValid;
};
function Formr({ data, onSave, def, autoSave = false, isSaving = false, validationCallback = () => {
    return { hasChanged: false, isValid: true };
}, formValidator = () => true, settings = formrSettings(), readonly = false, allowSaveWithoutChanges = false, onReset = () => { }, renderers: renderersProp }) {
    const providerValue = use(FormrCtx);
    const renderers = { ...shadcnRenderers, ...providerValue?.renderers, ...renderersProp };
    const [internalCopy, setInternalCopy] = useState(clone(data));
    const formrValues = useMemo(() => toFormrValue(internalCopy, def), [internalCopy, def]);
    const [hasChanges, setHasChanges] = useState(false);
    const [isValid, setIsValid] = useState(validate(formValidator, formrValues, internalCopy));
    const lastDataRef = useRef(clone(data));
    // Handle external data changes
    useEffect(() => {
        if (!diffInObjects(data, lastDataRef.current)) {
            return;
        }
        lastDataRef.current = clone(data);
        setInternalCopy(clone(data));
    }, [data]);
    // The autoSave that is waiting out its debounce, if any. Kept so a blur can
    // run it at once — see flushPendingSave.
    const pendingSaveRef = useRef(null);
    // Handle internal changes with debounce for autoSave
    useEffect(() => {
        const hasChanges = diffInObjects(internalCopy, lastDataRef.current);
        const currentFormrValues = toFormrValue(internalCopy, def);
        const valid = validate(formValidator, currentFormrValues, internalCopy);
        setHasChanges(hasChanges);
        setIsValid(valid);
        validationCallback?.(autoSave ? false : hasChanges, valid);
        if (autoSave && valid && hasChanges) {
            const save = () => {
                pendingSaveRef.current = null;
                onSave(clone(internalCopy));
                lastDataRef.current = clone(internalCopy);
            };
            pendingSaveRef.current = save;
            const timer = setTimeout(save, 300);
            return () => {
                clearTimeout(timer);
                if (pendingSaveRef.current === save)
                    pendingSaveRef.current = null;
            };
        }
        return undefined;
    }, [internalCopy, def, formValidator, autoSave, validationCallback, onSave]);
    /**
     * Run a debounced autoSave now, when focus leaves the form.
     *
     * The debounce keeps a parent from hearing every keystroke, but a parent
     * that saves from a button of its own then reads a value up to 300 ms old:
     * the edit dialog for event tickets sent the previous dog name and closed as
     * if it had saved. Leaving the form is the moment the user is done, and a
     * button click always blurs the field first, so the parent re-renders with
     * the current value before its click handler runs.
     */
    const flushPendingSave = () => {
        pendingSaveRef.current?.();
    };
    const handleReset = () => {
        setInternalCopy(clone(data));
        onReset();
    };
    const onUpdate = (key, value) => {
        setInternalCopy((oldCopy) => {
            return clone(setNestedValue(oldCopy, key, value));
        });
    };
    return (_jsxs("form", { autoComplete: "off", className: "w-full space-y-3", onBlur: autoSave ? flushPendingSave : undefined, children: [groupBySection(formrValues).map((group) => {
                const inputs = group.values.map((formrValue) => (_jsx(FormrInput, { formrValue: formrValue, onUpdate: onUpdate, readonly: readonly, renderers: renderers }, formrValue.key)));
                if (!group.section) {
                    return (_jsx(React.Fragment, { children: inputs }, `top-${group.values[0].key}`));
                }
                return (_jsx(FormrSection, { label: group.section, forceOpen: group.values.some((v) => !v.validator(v.value).isValid), children: inputs }, group.section));
            }), !autoSave && (_jsxs("div", { className: "sticky bottom-0 z-10 -mt-3 flex w-full justify-end gap-2 border-t border-border bg-background pt-3 pb-1", children: [!readonly && !settings.hideResetButton && (_jsx(Button, { variant: "outline", disabled: isSaving || (!hasChanges && !settings.alwaysEnableResetButton), onClick: handleReset, type: "button", children: settings.resetButtonText })), !settings.hideSaveButton && (_jsx(Button, { variant: "outline", disabled: readonly
                            ? false
                            : (!isValid && hasChanges) ||
                                isSaving ||
                                (!hasChanges && !allowSaveWithoutChanges), onClick: () => onSave(internalCopy), type: "button", children: settings.saveButtonText }))] }))] }));
}
export { Formr, toFormrDef };
//# sourceMappingURL=Formr.js.map